// Appointment Form Handler
// Handles form submission and email sending for appointment forms

import { sendAppointmentEmailsBatch, AppointmentFormData } from './brevoEmailService';
import {
    sendRecruitmentEmailsBatch,
    type RecruitmentFormData as RecruitmentFormDataType
} from './recruitmentEmailService';
import {
    sendBudgetEmailsBatch,
    type BudgetFormData as BudgetFormDataType
} from './budgetEmailService';
import {
    sendWecallEmailsBatch,
    type WecallFormData as WecallFormDataType
} from './wecallEmailService';
import { emailConfig } from '../config/emailConfig';
import { trackFormSuccess, mapServiceTypeToSubject } from '../utils/trackingUtils';

declare global {
    interface Window {
        showBudgetDialog?: (type: 'success' | 'error', message?: string) => void;
    }
}

/**
 * Handle appointment form submission
 */
export async function handleAppointmentFormSubmission(
    form: HTMLFormElement,
    language: string = 'pt'
): Promise<{ success: boolean; error?: string }> {
    try {
        const jQuery = (window as any).jQuery;
        if (!jQuery) {
            throw new Error('jQuery is not available');
        }

        // Get form data
        const formData: AppointmentFormData = {
            name: jQuery(form).find('[name="name"]').val() || '',
            email: jQuery(form).find('[name="email"]').val() || '',
            phone: jQuery(form).find('[name="phone"]').val() || jQuery(form).find('[name="Phone"]').val() || '',
            service: jQuery(form).find('[name="service"]').val() || '',
            date: jQuery(form).find('[name="date"]').val() || '',
            message: jQuery(form).find('[name="message"]').val() || '',
            language: language,
            ip_address: '',
            user_agent: navigator.userAgent || ''
        };

        // Validate required fields
        if (!formData.name || !formData.email || !formData.message) {
            const errorMsg = language === 'pt' 
                ? 'Por favor, preencha todos os campos obrigatórios.' 
                : 'Please fill in all required fields.';
            throw new Error(errorMsg);
        }

        // Validate privacy policy checkbox
        // Try multiple selectors to find the checkbox (supports different form implementations)
        const privacyCheckbox = (form.querySelector('#privacyPolicyContact') as HTMLInputElement) ||
                                (form.querySelector('#privacy_policy_contact') as HTMLInputElement) ||
                                (form.querySelector('[name="privacyPolicy"]') as HTMLInputElement) ||
                                (form.querySelector('[name="privacy_policy"]') as HTMLInputElement);
        if (!privacyCheckbox || !privacyCheckbox.checked) {
            const errorMsg = language === 'pt' 
                ? 'Por favor, aceite a Política de Privacidade para continuar.' 
                : 'Please accept the Privacy Policy to continue.';
            throw new Error(errorMsg);
        }

        // TEMPORARY: reCAPTCHA validation disabled for testing - uncomment block below to re-enable
        // reCAPTCHA validation - REQUIRED
        // const recaptchaContainer = form.querySelector('.g-recaptcha');
        // if (recaptchaContainer) {
        //     let recaptchaResponse = '';
        //     ...
        // }


        // Send both admin and user emails in a single batch (fixes reCAPTCHA token reuse issue)
        const batchResult = await sendAppointmentEmailsBatch(formData, language);
        
        if (!batchResult.success) {
            // Check if at least the admin email was sent
            const adminSent = batchResult.results?.[0]?.success;
            if (!adminSent) {
                throw new Error(batchResult.error || 'Failed to send emails');
            }
        }

        
        // Track form success for GTM/GA4
        await trackFormSuccess({
            form_type: 'contacto',
            form_subject: 'contacto',
            form_location: 'pagina_contacto',
            form_email: formData.email,
            form_phone: formData.phone
        });
        
        // Reset form
        form.reset();
        
        // Reset reCAPTCHA if present
        if (typeof (window as any).grecaptcha !== 'undefined') {
            (window as any).grecaptcha.reset();
        }

        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Initialize appointment form submission handler
 */
export function initializeAppointmentFormHandler(
    form: HTMLFormElement,
    language: string = 'pt'
): void {
    const jQuery = (window as any).jQuery;
    if (!jQuery) {
        setTimeout(() => initializeAppointmentFormHandler(form, language), 500);
        return;
    }

    // Remove existing handlers to prevent duplicates
    jQuery(form).off('submit');    // Add submit handler (optimistic UI: validate, show success, fire-and-forget the send)
    jQuery(form).on('submit', function(this: HTMLFormElement, e: any) {
        e.preventDefault();

        const formEl = this;
        const submitButton = jQuery(formEl).find('button[type="submit"]');
        const originalHtml = submitButton.html();
        const loadingText = language === 'pt' ? 'Enviando...' : 'Sending...';

        // Client-side validation (mirror of handleAppointmentFormSubmission checks)
        const jq = (window as any).jQuery;
        const name = jq(formEl).find('[name="name"]').val() || '';
        const email = jq(formEl).find('[name="email"]').val() || '';
        const message = jq(formEl).find('[name="message"]').val() || '';
        const privacyCheckbox = (formEl.querySelector('#privacyPolicyContact') as HTMLInputElement) ||
                                (formEl.querySelector('#privacy_policy_contact') as HTMLInputElement) ||
                                (formEl.querySelector('[name="privacyPolicy"]') as HTMLInputElement) ||
                                (formEl.querySelector('[name="privacy_policy"]') as HTMLInputElement);

        const showError = (msg: string) => {
            if ((window as any).showContactDialog) {
                (window as any).showContactDialog('error', msg);
            } else if (window.showBudgetDialog) {
                window.showBudgetDialog('error', msg);
            } else {
                alert(msg);
            }
        };

        if (!name || !email || !message) {
            showError(language === 'pt'
                ? 'Por favor, preencha todos os campos obrigatórios.'
                : 'Please fill in all required fields.');
            return;
        }
        if (!privacyCheckbox || !privacyCheckbox.checked) {
            showError(language === 'pt'
                ? 'Por favor, aceite a Política de Privacidade para continuar.'
                : 'Please accept the Privacy Policy to continue.');
            return;
        }

        // Show loading state briefly
        submitButton.prop('disabled', true);
        submitButton.html(loadingText + '<span class="btn-arrow"><i class="icon-next"></i></span>');

        const successMsg = language === 'pt'
            ? 'Obrigado! O seu contacto foi recebido com sucesso. Entraremos em contacto consigo brevemente.'
            : 'Thank you! Your contact has been successfully received. We will contact you shortly.';

        // Optimistic UI: success immediately, reset form, fire-and-forget the send
        if ((window as any).showContactDialog) {
            (window as any).showContactDialog('success', successMsg);
        } else if (window.showBudgetDialog) {
            window.showBudgetDialog('success');
        } else {
            alert(successMsg);
        }

        handleAppointmentFormSubmission(formEl, language).catch((err) => {
            console.error('Background mail send failed:', err);
        });

        submitButton.prop('disabled', false);
        submitButton.html(originalHtml);
    });
}

// ==================== RECRUITMENT / BUDGET / WECALL REACT HANDLERS ====================

// Get company name from environment or use default
function getCompanyName(): string {
    return emailConfig.BREVO_FROM_NAME || 'Roady Leiria';
}

export type RecruitmentFormData = RecruitmentFormDataType;
export type BudgetFormData = BudgetFormDataType;
export type WecallFormData = WecallFormDataType;

export async function submitRecruitmentForm(formData: RecruitmentFormData, language: string = 'pt', recaptchaToken?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const companyName = getCompanyName();

        // Upload CV to Firebase Storage for the download link in the template;
        // the File object is kept so the email service can also send it as an attachment
        let formDataWithStorage: RecruitmentFormData = { ...formData };

        if (formData.cvFile) {
            try {
                const { uploadCvToStorage } = await import('../utils/storageUtils');
                const { downloadUrl } = await uploadCvToStorage(
                    formData.cvFile,
                    formData.email
                );

                formDataWithStorage = {
                    ...formData,
                    cvDownloadUrl: downloadUrl,
                    cvFileName: formData.cvFile.name
                };

            } catch (uploadError) {
                // Non-fatal: the CV still reaches the admin as an email attachment
                console.warn('CV upload to Storage failed, sending as attachment only:', uploadError);
                formDataWithStorage = {
                    ...formData,
                    cvFileName: formData.cvFile.name
                };
            }
        }

        // Send both admin and user emails in a single batch (fixes reCAPTCHA token reuse issue)
        const batchResult = await sendRecruitmentEmailsBatch(formDataWithStorage as RecruitmentFormDataType, companyName, language, recaptchaToken);
        
        if (!batchResult.success) {
            const adminSent = batchResult.results?.[0]?.success;
            if (!adminSent) {
                throw new Error(batchResult.error || 'Failed to send emails');
            }
        }


        // Track form success for GTM/GA4
        await trackFormSuccess({
            form_type: 'recrutamento',
            form_subject: 'recrutamento',
            form_location: formData.form_location ?? 'pagina_recrutamento',
            form_email: formData.email,
            form_phone: formData.phone
        });

        return { success: true };
    } catch (error) {
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
        };
    }
}

export async function submitBudgetForm(formData: BudgetFormData, language: string = 'pt', recaptchaToken?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const companyName = getCompanyName();

        // Send both admin and user emails in a single batch (fixes reCAPTCHA token reuse issue)
        const batchResult = await sendBudgetEmailsBatch(formData as BudgetFormDataType, companyName, language, recaptchaToken);
        
        if (!batchResult.success) {
            const adminSent = batchResult.results?.[0]?.success;
            if (!adminSent) {
                throw new Error(batchResult.error || 'Failed to send emails');
            }
        }


        // Track form success for GTM/GA4
        await trackFormSuccess({
            form_type: 'orcamento',
            form_subject: mapServiceTypeToSubject(formData.serviceType),
            form_location: formData.form_location ?? 'pagina_orcamento',
            form_email: formData.email,
            form_phone: formData.phone
        });

        return { success: true };
    } catch (error) {
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
        };
    }
}

export async function submitWecallForm(formData: WecallFormData, language: string = 'pt', recaptchaToken?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const companyName = getCompanyName();

        // Send both admin and user emails in a single batch (fixes reCAPTCHA token reuse issue)
        const batchResult = await sendWecallEmailsBatch(formData as WecallFormDataType, companyName, language, recaptchaToken);
        
        if (!batchResult.success) {
            const adminSent = batchResult.results?.[0]?.success;
            if (!adminSent) {
                throw new Error(batchResult.error || 'Failed to send emails');
            }
        }


        // Track form success for GTM/GA4
        await trackFormSuccess({
            form_type: 'ligamos',
            form_subject: 'ligamos',
            form_location: formData.form_location ?? 'pagina_ligamos',
            form_email: formData.email,
            form_phone: formData.phone
        });

        return { success: true };
    } catch (error) {
        return { 
            success: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
        };
    }
}


